import { TURNSTILE_SCRIPT_URL, TURNSTILE_TEST_SITE_KEY } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { useTheme } from "@sagot/ui";
import { useEffect, useRef, useState } from "react";

interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

/** Loads the Turnstile script once, only when a form actually needs the challenge. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile unavailable"));
    };
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("turnstile failed to load"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/** Site key: configured key, or Cloudflare's always-pass test key in development. */
export function turnstileSiteKey(): string | null {
  return import.meta.env.VITE_TURNSTILE_SITE_KEY ?? (import.meta.env.DEV ? TURNSTILE_TEST_SITE_KEY : null);
}

interface TurnstileProps {
  action: string;
  onToken: (token: string | null) => void;
  /** Changing this re-issues the challenge (e.g. after a failed submit consumed the token). */
  resetKey?: number;
}

/** Cloudflare Turnstile widget (bot check on sign-up / repeated sign-in failures). */
export function Turnstile({ action, onToken, resetKey = 0 }: TurnstileProps) {
  const { t } = useTranslation();
  const { resolved } = useTheme();
  const container = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const callback = useRef(onToken);
  useEffect(() => {
    callback.current = onToken;
  });
  const siteKey = turnstileSiteKey();

  useEffect(() => {
    const node = container.current;
    if (!node || !siteKey) return undefined;
    let widgetId: string | null = null;
    let cancelled = false;
    loadTurnstile().then(
      (api) => {
        if (cancelled) return;
        widgetId = api.render(node, {
          sitekey: siteKey,
          action,
          theme: resolved,
          // Invisible unless Cloudflare needs an interaction (keeps the mockup layout).
          appearance: "interaction-only",
          callback: (token: string) => { callback.current(token); },
          "expired-callback": () => { callback.current(null); },
          "error-callback": () => { callback.current(null); },
        });
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
      callback.current(null);
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, action, resolved, resetKey]);

  if (!siteKey || failed) {
    return (
      <p role="alert" className="text-xs font-semibold text-danger">
        {t("auth.common.securityCheckFailed")}
      </p>
    );
  }
  return (
    <div>
      <span className="sr-only">{t("auth.common.securityCheck")}</span>
      <div ref={container} data-testid="turnstile" />
    </div>
  );
}
